/**
 * Phần logic thuần của màn vẽ ranh giới Vườn (Doc/09 lát 1b) — tách khỏi Leaflet/DOM để test
 * được trong môi trường `node` (xem apps/web/vitest.config.js). Hình học (kiểm hình, diện
 * tích) là của @farmer/audit-rules — cùng hàm API dùng để kiểm lại khi lưu.
 */
import { boundaryAreaHa, boundaryProblem, boundaryVertices, polygonFromVertices } from '@farmer/audit-rules';

/** Trung tâm mặc định khi Vườn chưa có toạ độ: Đắk Lắk — vùng điều tra chính. */
export const DEFAULT_CENTER = { lat: 12.7, lng: 108.05, zoom: 13 };

/**
 * Các Vườn của một Hộ, chia theo cách vẽ được lên bản đồ xem nhanh. Leaflet dùng [vĩ độ, kinh độ].
 *
 * @returns {{polygons: Array<{plot: object, latlngs: number[][]}>, points: Array<{plot: object, latlng: number[]}>}}
 */
export function plotMapItems(plots) {
  const polygons = [];
  const points = [];
  for (const plot of plots) {
    const vertices = boundaryVertices(plot.boundaryGeojson);
    if (vertices.length) {
      polygons.push({ plot, latlngs: vertices.map(([lng, lat]) => [lat, lng]) });
      continue;
    }
    const lat = plot.gpsLat === null || plot.gpsLat === undefined ? NaN : Number(plot.gpsLat);
    const lng = plot.gpsLng === null || plot.gpsLng === undefined ? NaN : Number(plot.gpsLng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) points.push({ plot, latlng: [lat, lng] });
  }
  return { polygons, points };
}

/** Mapbox cho phép phóng tới 22 với ảnh vệ tinh; OSM chuẩn dừng ở 19. */
const MAPBOX_MAX_ZOOM = 22;
const OSM_MAX_ZOOM = 19;

/**
 * Lớp nền. Có token Mapbox (biến build `VITE_MAPBOX_TOKEN`) → ảnh vệ tinh có nhãn đường
 * (`satellite-streets-v12`) — thấy được ranh vườn cà phê. Không có → OpenStreetMap, kèm
 * cảnh báo: chỉ đủ để chạy thử ở máy dev, không đủ để vẽ ranh vườn thật.
 *
 * Ô 512px + `zoomOffset: -1` là cách Mapbox khuyên dùng với Static Tiles API (bớt một nửa số
 * request so với ô 256px). `@2x` cho màn hình mật độ cao của điện thoại.
 */
export function basemapFor(token) {
  const t = String(token || '').trim();
  if (t) {
    return {
      kind: 'mapbox',
      url: `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/512/{z}/{x}/{y}@2x?access_token=${encodeURIComponent(t)}`,
      options: {
        tileSize: 512,
        zoomOffset: -1,
        maxZoom: MAPBOX_MAX_ZOOM,
        attribution:
          '© <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noopener">Mapbox</a> ' +
          '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> ' +
          '<a href="https://www.mapbox.com/map-feedback/" target="_blank" rel="noopener">Góp ý bản đồ</a>',
      },
      warning: null,
    };
  }
  return {
    kind: 'osm',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    options: {
      maxZoom: OSM_MAX_ZOOM,
      attribution: '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
    },
    warning: 'Chưa cấu hình VITE_MAPBOX_TOKEN — đang dùng bản đồ đường OpenStreetMap, không có ảnh vệ tinh.',
  };
}

/** Token rút gọn để người dùng đọc lại khi báo lỗi: "pk.eyJ1Ijo…a1b2" — đủ nhận ra token nào. */
export function maskToken(token) {
  const t = String(token || '').trim();
  return t.length > 16 ? `${t.slice(0, 10)}…${t.slice(-4)}` : t;
}

/**
 * Lý do một ô ảnh nền Mapbox không tải được, từ kết quả hỏi lại đúng URL ô đó (Doc/09 mục 8.9,
 * quyết định 10). Mapbox trả JSON `{ message }` kèm mã HTTP; 401/403/429 đều là token không
 * dùng được — nói thẳng ra để người dùng báo lại đúng chỗ. `status` = 0 / null: không gọi tới
 * được Mapbox (mất mạng), không phải lỗi token.
 *
 * @returns {{ tokenProblem: boolean, title: string, detail: string } | null} null nếu ô tải được
 */
export function describeTileFailure(status, mapboxMessage, token) {
  const said = mapboxMessage ? ` Mapbox trả lời: "${mapboxMessage}".` : '';
  const which = ` Token đang dùng: ${maskToken(token)}.`;
  const tokenFailure = (reason) => ({
    tokenProblem: true,
    title: 'Lỗi do token Mapbox không khả dụng — không tải được ảnh vệ tinh',
    detail: `HTTP ${status}: ${reason}.${said}${which} Báo quản trị hệ thống kèm nội dung này; tạm thời bản đồ chuyển sang OpenStreetMap (không có ảnh vệ tinh).`,
  });
  if (status === 401) return tokenFailure('token sai, đã bị thu hồi hoặc hết hạn (VITE_MAPBOX_TOKEN)');
  if (status === 403) return tokenFailure('token không được phép dùng ở địa chỉ này (giới hạn URL trong tài khoản Mapbox) hoặc thiếu quyền đọc kiểu bản đồ');
  if (status === 429) return tokenFailure('tài khoản Mapbox đã vượt hạn mức số lần tải ảnh');
  if (!status) {
    return {
      tokenProblem: false,
      title: 'Không kết nối được tới máy chủ bản đồ Mapbox',
      detail: 'Kiểm tra kết nối mạng rồi tải lại trang. Đây không phải lỗi token.',
    };
  }
  if (status >= 200 && status < 300) return null;
  return {
    tokenProblem: false,
    title: 'Máy chủ bản đồ Mapbox báo lỗi',
    detail: `HTTP ${status}.${said} Thử tải lại trang; còn lỗi thì báo quản trị hệ thống kèm nội dung này.`,
  };
}

/** Điểm nhìn ban đầu: ranh giới đã có → khung của nó; không thì toạ độ Vườn; không nữa thì mặc định. */
export function initialView(vertices, plot) {
  if (vertices.length) return { fitVertices: true };
  const lat = Number(plot?.gpsLat);
  const lng = Number(plot?.gpsLng);
  if (plot?.gpsLat != null && plot?.gpsLng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng, zoom: 18 };
  }
  return { ...DEFAULT_CENTER };
}

/**
 * Tình trạng hình đang vẽ, để hiện ngay dưới bản đồ. `declaredAreaHa` là diện tích khai báo
 * của Vườn — chênh nhiều thường là vẽ nhầm lô bên cạnh, hoặc diện tích khai báo đã cũ.
 *
 * @param {Array<[number, number]>} vertices - đỉnh `[kinh độ, vĩ độ]`, chưa khép vòng
 * @param {number|string|null} declaredAreaHa
 */
export function summarize(vertices, declaredAreaHa) {
  const polygon = polygonFromVertices(vertices);
  if (!polygon) {
    return { count: vertices.length, polygon: null, areaHa: 0, diffPct: null, problem: null, canSave: false };
  }
  const areaHa = boundaryAreaHa(polygon);
  const declared = Number(declaredAreaHa);
  const diffPct = declared > 0 ? ((areaHa - declared) / declared) * 100 : null;
  const problem = boundaryProblem(polygon);
  return { count: vertices.length, polygon, areaHa, diffPct, problem, canSave: !problem };
}

/** Ngưỡng chênh lệch diện tích (%) để nhắc kiểm lại — chỉ cảnh báo, không chặn lưu. */
export const AREA_DIFF_WARN_PCT = 20;

/** Dòng mô tả ngắn: "5 đỉnh · 1,18 ha (khai báo 1,2 ha, −2%)". */
export function summaryText(s, declaredAreaHa) {
  if (s.count < 3) return s.count ? `${s.count} đỉnh — chạm thêm ${3 - s.count} điểm để thành hình` : 'Chạm lên bản đồ để đặt đỉnh đầu tiên';
  const fmt = (n) => Number(n).toLocaleString('vi-VN', { maximumFractionDigits: 2 });
  const diff =
    s.diffPct == null
      ? ''
      : ` (khai báo ${fmt(declaredAreaHa)} ha, ${s.diffPct >= 0 ? '+' : '−'}${fmt(Math.abs(s.diffPct))}%)`;
  return `${s.count} đỉnh · ${fmt(s.areaHa)} ha${diff}`;
}
